import { ChangeDetectionStrategy, Component, ElementRef, inject, input, Renderer2 } from '@angular/core';
import type { OnChanges, SimpleChanges } from '@angular/core';
import { BUTTON_RESIZE_MAP, type DsButtonNativeType, type DsButtonSize } from '@skyeng/libs/shared/ui-kit-v2/button';
import { DsBreakpointService } from '@skyeng/libs/shared/ui-kit-v2/layout';
import { DsSpinnerModule, type DsSpinnerColor, type DsSpinnerSize } from '@skyeng/libs/shared/ui-kit-v2/spinner';
import { dsUpdateComponentCssVars } from '@skyeng/libs/shared/ui-kit-v2/theme';

const BUTTON_CSS_VARS_PREFIX = 'button';
const BUTTON_CUSTOMISABLE_PARAMS = [
  'color',
  'iconColor',
  'backgroundColor',
  'colorHover',
  'iconColorHover',
  'backgroundColorHover',
  'colorPressed',
  'iconColorPressed',
  'backgroundColorPressed',
  'colorDisabled',
  'iconColorDisabled',
  'backgroundColorDisabled',
];
const BUTTON_FONT_CUSTOMISABLE_PARAMS = [
  'desktop-fontSize',
  'desktop-fontWeight',
  'desktop-lineHeight',
  'desktop-letterSpacing',
  'mobile-fontSize',
  'mobile-fontWeight',
  'mobile-lineHeight',
  'mobile-letterSpacing',
];

@Component({
  selector: 'lab-button',
  imports: [DsSpinnerModule],
  templateUrl: './lab-button.component.html',
  styleUrls: ['./lab-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabButtonComponent implements OnChanges {
  readonly round = input(false);
  readonly stretchContent = input(false);
  readonly flush = input(false);
  readonly ariaControls = input<string>();
  readonly ariaExpanded = input<boolean>();
  readonly ariaCurrent = input<string>();
  readonly ariaLabel = input<string>();
  readonly disabled = input(false);
  readonly size = input<DsButtonSize>('s');
  readonly isAloneIcon = input(false);
  /**
   * Ключ набора CSS-переменных из темы Laboratory: `primary`, `secondary`, `ghost`, `inverted`,
   * `base`, `transparent`, `link`, `link-negative`.
   *
   * У обоих `link` коробки кнопки нет, и `size` им ничего не задаёт: типографику текстового
   * действия ставит сам тип.
   */
  readonly type = input<string>();
  readonly nativeType = input<DsButtonNativeType>();
  readonly spinner = input(false);
  readonly spinnerColor = input<DsSpinnerColor>();
  readonly resizeMap = input(BUTTON_RESIZE_MAP);

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly dsBreakpointService = inject(DsBreakpointService);

  isMobile = this.dsBreakpointService.isSmall();

  ngOnChanges(changes: SimpleChanges): void {
    const type = this.type();

    if (changes.type && type !== undefined) {
      dsUpdateComponentCssVars({
        renderer: this.renderer,
        rootElement: this.elementRef,
        params: BUTTON_CUSTOMISABLE_PARAMS,
        componentPrefix: BUTTON_CSS_VARS_PREFIX,
        type,
      });

      if (changes.type.firstChange) {
        this.setFontCssVars();
      }
    }

    if (changes.size) {
      this.setFontCssVars();
    }
  }

  checkPropagation(event: MouseEvent): void {
    if (this.disabled() || this.spinner()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  getSize(): DsButtonSize {
    return this.isMobile ? this.resizeMap()[this.size()] : this.size();
  }

  getSpinnerSize(): DsSpinnerSize {
    return ['m', 'l', 'xl'].includes(this.size()) ? 'm' : 's';
  }

  getSpinnerColor(): DsSpinnerColor {
    return this.spinnerColor() ?? 'custom';
  }

  private setFontCssVars(): void {
    dsUpdateComponentCssVars({
      renderer: this.renderer,
      rootElement: this.elementRef,
      params: BUTTON_FONT_CUSTOMISABLE_PARAMS,
      componentPrefix: BUTTON_CSS_VARS_PREFIX,
      type: this.getSize(),
    });
  }
}
